import { useEffect, useState } from 'react'
import Hero from '../components/Hero'
import { useParams } from 'react-router-dom'
import { FaHeart } from 'react-icons/fa'
import DonationCard from '../components/DonationCard'
import { usePageTitle } from '../../shared/components/PageTitleContext'
import { useTranslation } from 'react-i18next'
import { useCart } from '../../util/CartContext'
import { fetchWithLanguage } from '../../util/fetchFunction'
import { languageUtil } from '../../util/languageUtils'
import { ErrorOverlay, Loading, LoadingOverlay } from '../../shared/components/Loading'
import SuccessModal from '../components/SuccessModal'
import CartModal from '../components/CartModal'

const NewsSlug = () => {
    const { id } = useParams()
    const [news, setNews] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
    const [isCartModalOpen, setIsCartModalOpen] = useState(false)

    const { setTitle } = usePageTitle()
    const { t } = useTranslation()
    const { cart, addDonationItem } = useCart()
    const { language } = languageUtil()

    const onDonationChange = async data => {
        try {
            await addDonationItem({
                data,
                id,
                type: 'news_appeal',
                onSuccess: () => setIsSuccessModalOpen(true),
                onError: error => () => {
                    setError(`Donation failed: ${error}`)
                    console.error('Donation failed:', error)
                },
            })
        } catch (error) {
            setError(`Unexpected error: ${error}`)
            console.error('Unexpected error:', error)
        }
    }

    useEffect(() => {
        const fetchNews = async () => {
            await fetchWithLanguage(`get-news-detail/${id}`, setNews, setError, setLoading)
        }

        fetchNews()
    }, [id, language])

    useEffect(() => {
        if (news?.title) {
            setTitle(`Strive2Help - ${news.title || news.title_en}`)
        }
    }, [news?.title, setTitle])

    return (
        <div>
            {loading ? (
                <Loading />
            ) : (
                <div className='relative'>
                    {cart.isLoading && <LoadingOverlay />}
                    {error && <ErrorOverlay message={error} onClose={() => setError(null)} />}

                    <Hero
                        title={news.title || news.title_en}
                        imageUrl={news.header_image || news.image_preview}
                        size='large'
                    />
                    <SuccessModal
                        isOpen={isSuccessModalOpen}
                        onClose={() => setIsSuccessModalOpen(false)}
                        onOpenCart={() => setIsCartModalOpen(true)}
                    />
                    <CartModal isOpen={isCartModalOpen} onClose={() => setIsCartModalOpen(false)} />

                    <div className='relative flex flex-col mx-auto lg:flex-row max-w-7xl'>
                        {/* Background decorative elements */}
                        <div className='absolute top-0 left-0 w-64 h-64 -translate-x-32 -translate-y-32 rounded-full bg-gradient-to-br from-blue-100/20 to-indigo-100/20 blur-3xl'></div>
                        <div className='absolute bottom-0 right-0 translate-x-48 translate-y-48 rounded-full w-96 h-96 bg-gradient-to-br from-purple-100/10 to-pink-100/10 blur-3xl'></div>

                        {/* Left Column - Donation Card (Sticky) */}
                        <div className='w-full px-4 py-8 lg:w-[400px] xl:w-[450px] lg:sticky top-24 self-start'>
                            <DonationCard donationData={news} onAmountChange={onDonationChange} />
                        </div>

                        {/* Right Column - Content */}
                        <div className='flex-1 max-w-3xl px-4 py-8 mx-auto lg:px-8 lg:py-12'>
                            {/* News Subtitle */}
                            {news.subtitle || news.subtitle_en ? (
                                <div className='mb-8 text-center lg:text-left'>
                                    <h2 className='relative inline-block text-3xl font-bold text-gray-900 lg:text-4xl'>
                                        <span className='relative z-10'>
                                            {news.subtitle || news.subtitle_en}
                                        </span>
                                        <span className='absolute left-0 w-full h-3 transform rounded-full -bottom-2 bg-gradient-to-r from-blue-200/50 to-indigo-200/50 -rotate-1'></span>
                                    </h2>
                                    <div className='w-20 h-1 mx-auto mt-4 rounded-full lg:mx-0 bg-gradient-to-r from-blue-400 to-indigo-400'></div>
                                </div>
                            ) : null}

                            {/* News Description */}
                            <div className='mb-8'>
                                <div className='p-6 border border-blue-100 shadow-sm lg:p-8 bg-gradient-to-br from-white to-blue-50/30 rounded-2xl'>
                                    <div className='flex items-start gap-3 mb-4'>
                                        <div className='w-2 h-2 mt-2 bg-blue-400 rounded-full'></div>
                                        <h3 className='text-lg font-semibold text-gray-900'>
                                            {t('donation.story')}
                                        </h3>
                                    </div>
                                    <p className='text-lg leading-relaxed text-gray-700'>
                                        <div
                                            className='text-lg leading-relaxed text-gray-700'
                                            dangerouslySetInnerHTML={{
                                                __html: news.description || news.description_en,
                                            }}
                                        />
                                    </p>
                                </div>
                            </div>

                            {/* Urgency Message */}
                            {news.urgency_message && (
                                <div className='mb-8'>
                                    <div className='p-6 border border-red-200 shadow-sm bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl'>
                                        <div className='flex items-start gap-3 mb-3'>
                                            <div className='w-3 h-3 mt-1 bg-red-500 rounded-full animate-pulse'></div>
                                            <h3 className='text-lg font-bold text-red-900'>
                                                {t('news.urgent_update')}
                                            </h3>
                                        </div>
                                        <div className='flex items-start gap-3'>
                                            <svg
                                                className='w-5 h-5 text-red-500 mt-0.5 flex-shrink-0'
                                                fill='currentColor'
                                                viewBox='0 0 20 20'
                                            >
                                                <path
                                                    fillRule='evenodd'
                                                    d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                                                    clipRule='evenodd'
                                                />
                                            </svg>
                                            <p className='font-medium text-red-800'>
                                                {news.urgency_message || news.urgency_message_en}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Additional Content */}
                            {news.additional_content && (
                                <div className='mb-12'>
                                    <div className='p-6 bg-white border border-gray-200 shadow-sm lg:p-8 rounded-2xl'>
                                        <div className='flex items-start gap-3 mb-6'>
                                            <div className='w-2 h-2 mt-2 bg-purple-400 rounded-full'></div>
                                            <h3 className='text-lg font-semibold text-gray-900'>
                                                {t('news.full_story')}
                                            </h3>
                                        </div>
                                        <div className='prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-a:text-blue-600 hover:prose-a:text-blue-700'>
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        news.additional_content ||
                                                        news.additional_content_en,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Impact Sections */}
                            {news.impact_sections?.length > 0 && (
                                <section className='mt-16'>
                                    <div className='mb-10 text-center lg:text-left'>
                                        <h3 className='relative inline-block text-2xl font-bold text-gray-900 lg:text-3xl'>
                                            <span className='relative z-10'>Our Impact</span>
                                            <span className='absolute left-0 w-full h-3 transform rounded-full -bottom-2 bg-gradient-to-r from-emerald-200/50 to-green-200/50 -rotate-1'></span>
                                        </h3>
                                        <p className='max-w-2xl mt-4 text-gray-600'>
                                            See how your support is making a difference
                                        </p>
                                    </div>

                                    <div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'>
                                        {news.impact_sections.map((item, index) => (
                                            <div
                                                key={index}
                                                className='relative p-6 transition-all duration-300 border group bg-gradient-to-br from-white to-emerald-50 rounded-xl border-emerald-100 hover:border-emerald-300 hover:-translate-y-1 hover:shadow-lg'
                                            >
                                                {/* Decorative corner */}
                                                <div className='absolute w-2 h-2 rounded-full opacity-50 top-3 right-3 bg-emerald-300'></div>

                                                {/* Icon container */}
                                                <div className='relative mb-4'>
                                                    <div className='absolute inset-0 rounded-full bg-gradient-to-br from-emerald-400/20 to-green-400/20 blur-md'></div>
                                                    <div className='relative flex items-center justify-center w-12 h-12 mx-auto rounded-full shadow-lg bg-gradient-to-br from-emerald-500 to-green-500'>
                                                        <FaHeart className='text-xl text-white' />
                                                    </div>
                                                </div>

                                                {/* Content */}
                                                <div className='space-y-3 text-center'>
                                                    <h4 className='text-lg font-bold text-gray-900'>
                                                        {item.title || item.title_en}
                                                    </h4>
                                                    <p className='text-gray-600'>
                                                        {item.description || item.description_en}
                                                    </p>
                                                </div>

                                                {/* Hover effect */}
                                                <div className='absolute inset-0 transition-opacity duration-300 border-2 opacity-0 pointer-events-none border-emerald-300 rounded-xl group-hover:opacity-100'></div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* Call to Action Section */}
                            {/* <div className='p-8 mt-16 text-center border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl'>
                                <div className='max-w-2xl mx-auto'>
                                    <h3 className='mb-3 text-2xl font-bold text-gray-900'>
                                        Support This Cause
                                    </h3>
                                    <p className='mb-6 text-gray-700'>
                                        Your contribution helps us continue this important work and
                                        create meaningful impact.
                                    </p>
                                    <button
                                        onClick={() =>
                                            document
                                                .querySelector('.lg\\:sticky')
                                                ?.scrollIntoView({ behavior: 'smooth' })
                                        }
                                        className='inline-flex items-center gap-2 px-8 py-3 font-bold text-white transition-all duration-300 transform rounded-lg shadow-lg bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 hover:scale-105 hover:shadow-xl'
                                    >
                                        <svg
                                            className='w-5 h-5'
                                            fill='none'
                                            stroke='currentColor'
                                            viewBox='0 0 24 24'
                                        >
                                            <path
                                                strokeLinecap='round'
                                                strokeLinejoin='round'
                                                strokeWidth='2'
                                                d='M12 8v8m-4-4h8m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                                            ></path>
                                        </svg>
                                        Donate Now
                                    </button>
                                    <p className='mt-4 text-sm text-gray-600'>
                                        100% of your donation goes directly to support this
                                        initiative
                                    </p>
                                </div>
                            </div> */}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default NewsSlug
